<script setup>
import {ref, computed, onMounted, onUnmounted, watch} from 'vue'
import {useStore} from 'vuex'
import { MapSource, MapLayer } from '@/components/map'
import { HOST, API } from '@/utils/http'
import LayerCollectedDataPopup from './LayerCollectedDataPopup.vue'
import { useToast } from '@/components/ui/toast'
import { useLayerManager } from '@/composables/useLayerManager'
import { createLayerId, createSourceId } from '@/components/layers/types'
import { useLayerFetch } from '@/components/layers/useLayerFetch'

const { toast } = useToast()
const store = useStore()
const settings = computed(() => store.getters['user/getUserSettings'])
const map = computed(() => props.map)
const selectedFeature = computed(() => store.state.layers.selectedFeature)
const layerManager = useLayerManager(map)

const emit = defineEmits(['isLoaded'])
const props = defineProps({
  map: {
    type: Object,
    required: true
  },
  layerId: {
    type: String,
    required: true
  },
  beforeId: {
    type: String,
    required: true
  },
  groupId: {
    type: String,
    required: true
  },
  code: {
    type: String,
    required: true
  },
  layer: {
    type: Object,
    required: true
  },
  orgId: {
    type: Number,
    required: true
  },
  isVisible: {
    type: Boolean,
    default: true
  }
})

const isPopupVisible = ref(false)
const popupCoordinates = ref(null)
const popupFeature = ref(null)
const popupCounter = ref(0)

const storeLayer = computed(() => {
  const orgId = props.orgId
  return store.getters['layers/getOrgLayerById'](orgId, props.code)
})

const features = computed(() => {
  const result = storeLayer.value?.properties?.features || [];
  return result;
})

const featureCollection = computed(() => {
  if (storeLayer.value?.properties?.featureCollection) {
    return storeLayer.value.properties.featureCollection;
  }
  return {
    type: 'FeatureCollection',
    features: features.value
  };
})

const isLoadingFeatures = ref(false)
const featuresCount = computed(() => features.value.length)

// Unified layer IDs
const sourceId = computed(() => createSourceId(props.groupId, 'source'))
const polygonLayerId = computed(() => createLayerId(props.groupId, 'polygon-outline'))
const lineLayerId = computed(() => createLayerId(props.groupId, 'line'))
const pointLayerId = computed(() => createLayerId(props.groupId, 'point'))

const showPopup = (e) => {
  if (!e.features || e.features.length === 0) return

  const feature = e.features[0]
  popupFeature.value = feature
  popupCoordinates.value = e.lngLat.toArray()
  isPopupVisible.value = true
  popupCounter.value++
}

const closePopup = () => {
  isPopupVisible.value = false
  popupFeature.value = null
}

const setupLayerEvents = () => {
  if (!map.value) return

  const setupLayerClickEvent = (layerId) => {
    map.value.on('click', layerId, showPopup)
    map.value.on('mouseenter', layerId, () => {
      map.value.getCanvas().style.cursor = 'pointer'
    })

    map.value.on('mouseleave', layerId, () => {
      map.value.getCanvas().style.cursor = ''
    })
  }

  setupLayerClickEvent(pointLayerId.value)
  setupLayerClickEvent(lineLayerId.value)
  setupLayerClickEvent(polygonLayerId.value)
}

const fetchFeatures = async () => {
  isLoadingFeatures.value = true;

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${props.orgId}/layers/${props.code}/static/features`
    });


    if (response.data?.type === 'FeatureCollection' && Array.isArray(response.data.features)) {
      const orgId = props.orgId;
      const layerId = props.code;

      await store.dispatch('layers/updateOrgLayerFeatures', {
        orgId,
        layerId,
        features: response.data.features
      });

      emit('isLoaded', true);
    }
  } catch (error) {
    console.error('Error loading static layer features:', error);
    toast({
      title: 'Error loading features',
      description: 'Failed to fetch features for the static layer',
      variant: 'destructive'
    });
  } finally {
    isLoadingFeatures.value = false;
  }
}

watch(selectedFeature, (newSelectedFeature) => {
  if (newSelectedFeature?.feature && newSelectedFeature.isVisible &&
      newSelectedFeature.feature.properties?.code &&
      features.value.some(f => f.properties?.code === newSelectedFeature.feature.properties.code)) {

    popupFeature.value = newSelectedFeature.feature
    popupCoordinates.value = newSelectedFeature.coordinates
    isPopupVisible.value = true
    popupCounter.value++
  }
}, { deep: true })

watch(() => [map.value, featuresCount.value], ([newMap, newCount]) => {
  if (newMap && newCount > 0) {
    setTimeout(() => {
      setupLayerEvents();
    }, 200);
  }
}, { immediate: true });

useLayerFetch(computed(() => props.isVisible), {
  onShow: () => {
    if (isLoadingFeatures.value || featuresCount.value > 0) return
    fetchFeatures()
  }
})

onMounted(() => {
  // Register layers with layer manager
  layerManager.registerLayer(props.groupId, polygonLayerId.value)
  layerManager.registerLayer(props.groupId, lineLayerId.value)
  layerManager.registerLayer(props.groupId, pointLayerId.value)
})

onUnmounted(() => {
  // Unregister layers from layer manager
  layerManager.unregisterLayer(props.groupId, pointLayerId.value)
  layerManager.unregisterLayer(props.groupId, lineLayerId.value)
  layerManager.unregisterLayer(props.groupId, polygonLayerId.value)
})
</script>

<template>
  <MapSource
    v-if="featuresCount > 0 && map"
    :map="map"
    :id="sourceId"
    type="geojson"
    :data="featureCollection"
  >
    <!-- Chained beforeId for correct sublayer ordering (bottom to top) -->
    <!-- 1. Polygon outlines - bottommost, uses parent beforeId -->
    <MapLayer
      v-if="map"
      :map="map"
      :id="polygonLayerId"
      :source="sourceId"
      type="line"
      :before-id="beforeId"
      :group-id="groupId"
      :options="{
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: {
          'line-color': ['coalesce', ['get', 'color'], '#0059ff'],
          'line-width': 2,
          'line-opacity': 0.8
        },
        layout: {
          visibility: isVisible ? 'visible' : 'none'
        }
      }"
    />

    <!-- 2. Lines - above polygon outlines -->
    <MapLayer
      v-if="map"
      :map="map"
      :id="lineLayerId"
      :source="sourceId"
      type="line"
      :before-id="polygonLayerId"
      :group-id="groupId"
      :options="{
        filter: ['==', ['geometry-type'], 'LineString'],
        paint: {
          'line-width': 3,
          'line-color': ['coalesce', ['get', 'color'], '#0059ff'],
          'line-opacity': 0.8
        },
        layout: {
          visibility: isVisible ? 'visible' : 'none'
        }
      }"
    />

    <!-- 3. Points - topmost, above lines -->
    <MapLayer
      v-if="map"
      :map="map"
      :id="pointLayerId"
      :source="sourceId"
      type="circle"
      :before-id="lineLayerId"
      :group-id="groupId"
      :options="{
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-radius': 6,
          'circle-color': ['coalesce', ['get', 'color'], '#0059ff'],
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 0.8
        },
        layout: {
          visibility: isVisible ? 'visible' : 'none'
        }
      }"
    />

    <LayerCollectedDataPopup
      v-if="isPopupVisible && popupCoordinates"
      :map="map"
      :is-visible="isPopupVisible"
      :coordinates="popupCoordinates"
      :feature="popupFeature"
      :counter="popupCounter"
      :settings="settings"
      :code="props.code"
      @close="closePopup"
    />
  </MapSource>
</template>
